/**
 * Central icon module (Item 5). Maps SEMANTIC names to Tabler components so the
 * rest of the app never imports from @tabler/icons-react directly — one file to
 * change if the library is ever swapped.
 *
 * Defaults: 2px stroke, currentColor (icons inherit their context's colour — no
 * hard-coded colours here), size 18 (good for badges/table rows). Override
 * `size` at nav/buttons (20). Decorative icons should get aria-hidden by the
 * caller; icon-only buttons a real aria-label.
 *
 * Per-icon imports keep the bundle tree-shakeable.
 */

import React from 'react';
import {
  IconX, IconTrash, IconPencil, IconCheck, IconBell, IconBellOff, IconAlertTriangle,
  IconStarFilled, IconStar, IconCoin, IconPhone, IconLayoutDashboard, IconReportMoney,
  IconUsers, IconUser, IconUserStar, IconClock, IconMessage, IconMapPin, IconPin,
  IconMail, IconCircleCheck, IconCalendar, IconChartLine, IconFolder, IconSettings,
  IconKey, IconInbox, IconCreditCard, IconTag, IconBuilding, IconTruck, IconPlayerPause,
  IconPlayerPlay, IconFileText, IconInfoCircle, IconBan, IconSignature, IconBox,
  IconWorld, IconConfetti, IconSparkles, IconFileInvoice, IconArrowLeft, IconMapPinFilled,
  IconSquareCheck, IconSquare, IconChevronDown, IconChevronRight, IconChevronUp,
  IconEye, IconClick, IconSearch, IconClipboardText, IconDownload, IconSend, IconExternalLink,
  IconArchive, IconArmchair, IconArmchair2, IconBallBasketball, IconBarbell, IconBasket, IconBed, IconBedFlat, IconBike, IconBlender, IconBooks, IconBoxMultiple, IconBriefcase, IconBroadcast, IconBuildingMonument, IconBuildingWarehouse, IconCar, IconCircle, IconCircleDot, IconCylinder, IconDeviceDesktop, IconDeviceTv, IconDroplet, IconFish, IconFlame, IconFridge, IconHanger, IconLamp, IconLamp2, IconLayoutGrid, IconLock, IconMicrowave, IconMotorbike, IconNeedle, IconPaw, IconPhoto, IconPiano, IconPlant, IconPlant2, IconPool, IconPrinter, IconRun, IconShirt, IconShoppingCart, IconShovel, IconSnowflake, IconSofa, IconSpeakerphone, IconStairs, IconTable, IconTool, IconTree, IconTrophy, IconUmbrella, IconWash, IconWashMachine, IconWaveSine, IconWeight, IconWind,
  IconBath, IconCarGarage, IconChefHat, IconPackage, IconStairsDown, IconToolsKitchen2,
  IconHomeMove, IconTruckLoading, IconShieldCheck, IconHeadset,
  IconClockDollar, IconRoute, IconMoodSmile,
  type IconProps,
} from '@tabler/icons-react';

// Wrap so every icon defaults to size 18 / stroke 2 and stays overridable.
const make = (Cmp: React.ComponentType<IconProps>) => {
  const Wrapped = (props: IconProps) => <Cmp size={18} stroke={2} {...props} />;
  return Wrapped;
};

export const Icons = {
  // actions
  close: make(IconX),
  delete: make(IconTrash),
  edit: make(IconPencil),
  check: make(IconCheck),
  ban: make(IconBan),
  // nav / sections
  dashboard: make(IconLayoutDashboard),
  clients: make(IconUsers),
  calendar: make(IconCalendar),
  stats: make(IconChartLine),
  settings: make(IconSettings),
  folder: make(IconFolder),
  money: make(IconCoin),
  // awareness layer
  newLead: make(IconSparkles),
  notification: make(IconBell),
  bellOn: make(IconBell),
  bellOff: make(IconBellOff),
  followUp: make(IconClock),
  clock: make(IconClock),
  overdue: make(IconAlertTriangle),
  message: make(IconMessage),
  world: make(IconWorld),
  celebrate: make(IconConfetti),
  // people
  user: make(IconUser),
  reps: make(IconUserStar),
  key: make(IconKey),
  // lead / contact
  phone: make(IconPhone),
  email: make(IconMail),
  origin: make(IconMapPin),
  destination: make(IconMapPinFilled),
  pinned: make(IconPin),
  unpinned: make(IconMapPin),
  // money / docs
  invoice: make(IconFileInvoice),
  document: make(IconFileText),
  signature: make(IconSignature),
  card: make(IconCreditCard),
  box: make(IconBox),
  // status
  accepted: make(IconCircleCheck),
  caughtUp: make(IconCircleCheck),
  warning: make(IconAlertTriangle),
  info: make(IconInfoCircle),
  star: make(IconStar),
  starFilled: make(IconStarFilled),
  back: make(IconArrowLeft),
  // settings entities
  pricing: make(IconReportMoney),
  building: make(IconBuilding),
  truck: make(IconTruck),
  // estimate portal — "Everything included in your move"
  roomToRoom: make(IconHomeMove),
  tools: make(IconTool),
  loading: make(IconTruckLoading),
  wrapped: make(IconPackage),
  coverage: make(IconShieldCheck),
  coordinator: make(IconHeadset),
  hourly: make(IconClockDollar),
  route: make(IconRoute),
  friendly: make(IconMoodSmile),
  tag: make(IconTag),
  inbox: make(IconInbox),
  pause: make(IconPlayerPause),
  play: make(IconPlayerPlay),
  checkboxOn: make(IconSquareCheck),
  checkboxOff: make(IconSquare),
  chevronDown: make(IconChevronDown),
  chevronRight: make(IconChevronRight),
  chevronUp: make(IconChevronUp),
  eye: make(IconEye),       // email opened (soft signal)
  click: make(IconClick),   // link clicked (reliable signal)
  search: make(IconSearch),
  audit: make(IconClipboardText), // Audit Report (send history)
  download: make(IconDownload),
  send: make(IconSend),
  openExternal: make(IconExternalLink),
};

export type IconName = keyof typeof Icons;


/**
 * CATALOG ICONS — inventory_catalog.icon -> a component.
 *
 * The 170-item catalog seeded in 005 carries an icon name on every row (65
 * distinct), and nothing has ever rendered one: InventoryPanel imported this
 * module only for its close button. The client-facing room-by-room page is a
 * picture-led grid, so the names finally have a use.
 *
 * LINE ICONS, NOT PHOTOGRAPHY. Real item photos would need an image column, an
 * asset host and 170 pictures; a line icon is recognisable enough to pick a sofa
 * out of a grid and costs one import. If photography ever happens it replaces
 * this map, not the schema.
 *
 * TWO NAMES HAVE NO TABLER COMPONENT and are mapped to the nearest one that
 * does — 'basketball' -> IconBallBasketball, 'cage' -> IconBoxMultiple (pet
 * crates and similar). Substituted here rather than by editing the seed, so the
 * database keeps the name the catalog author chose.
 *
 * UNKNOWN NAMES FALL BACK rather than throwing or rendering nothing: a catalog
 * row someone adds by hand with a typo still shows a box, and the grid keeps
 * its shape.
 */
const CATALOG_ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  'archive': IconArchive,
  'armchair': IconArmchair,
  'armchair-2': IconArmchair2,
  'barbell': IconBarbell,
  'basket': IconBasket,
  'basketball': IconBallBasketball,
  'bed': IconBed,
  'bed-flat': IconBedFlat,
  'bike': IconBike,
  'blender': IconBlender,
  'books': IconBooks,
  'box': IconBox,
  'briefcase': IconBriefcase,
  'broadcast': IconBroadcast,
  'building': IconBuilding,
  'building-monument': IconBuildingMonument,
  'building-warehouse': IconBuildingWarehouse,
  'cage': IconBoxMultiple,
  'car': IconCar,
  'circle': IconCircle,
  'circle-dot': IconCircleDot,
  'clock': IconClock,
  'cylinder': IconCylinder,
  'device-desktop': IconDeviceDesktop,
  'device-tv': IconDeviceTv,
  'droplet': IconDroplet,
  'fish': IconFish,
  'flame': IconFlame,
  'fridge': IconFridge,
  'hanger': IconHanger,
  'lamp': IconLamp,
  'lamp-2': IconLamp2,
  'layout-grid': IconLayoutGrid,
  'lock': IconLock,
  'microwave': IconMicrowave,
  'motorbike': IconMotorbike,
  'needle': IconNeedle,
  'paw': IconPaw,
  'photo': IconPhoto,
  'piano': IconPiano,
  'plant': IconPlant,
  'plant-2': IconPlant2,
  'pool': IconPool,
  'printer': IconPrinter,
  'run': IconRun,
  'shirt': IconShirt,
  'shopping-cart': IconShoppingCart,
  'shovel': IconShovel,
  'snowflake': IconSnowflake,
  'sofa': IconSofa,
  'sparkles': IconSparkles,
  'speakerphone': IconSpeakerphone,
  'square': IconSquare,
  'stairs': IconStairs,
  'table': IconTable,
  'tool': IconTool,
  'trash': IconTrash,
  'tree': IconTree,
  'trophy': IconTrophy,
  'umbrella': IconUmbrella,
  'wash': IconWash,
  'wash-machine': IconWashMachine,
  'wave-sine': IconWaveSine,
  'weight': IconWeight,
  'wind': IconWind,
  // The ROOM icons from inventory_rooms.icon. Six names the item catalog does
  // not use, folded into the same map so one lookup serves both.
  'bath': IconBath,
  'car-garage': IconCarGarage,
  'chef-hat': IconChefHat,
  'package': IconPackage,
  'stairs-down': IconStairsDown,
  'tools-kitchen-2': IconToolsKitchen2,
};

/**
 * `name` IS OMITTED FROM IconProps BEFORE BEING REDECLARED. Tabler's own props
 * carry a `name?: string`, and intersecting rather than replacing it left the
 * prop typed `string | undefined` — so every caller passing a column straight
 * from the payload (`icon: string | null`) failed to compile and had to write
 * `?? undefined` at the call site. Null is the value the database actually
 * holds for an un-iconed row, and it is exactly what the fallback below is for.
 */
type IconByName = Omit<IconProps, 'name'> & { name?: string | null };

export const CatalogIcon = ({ name, ...props }: IconByName) => {
  const Cmp = (name && CATALOG_ICON_MAP[name]) || IconBox;
  return <Cmp size={18} stroke={2} {...props} />;
};

/** Room key -> icon, from inventory_rooms.icon. Same fallback rule. */
export const RoomIcon = ({ name, ...props }: IconByName) => {
  const Cmp = (name && CATALOG_ICON_MAP[name]) || IconLayoutGrid;
  return <Cmp size={18} stroke={2} {...props} />;
};

/**
 * CATALOG ARTWORK (P5b-1) — the illustration for an item, with the icon behind
 * it. `src` is inventory_catalog.image_url, which is null until artwork has been
 * uploaded and the linking script has run.
 *
 * THE BOX IS RESERVED BEFORE THE IMAGE ARRIVES. A room grid is ~40 of these and
 * a client on a phone scrolls it while it loads; images that size themselves on
 * arrival would reflow the grid under their thumb, which on a tap target means
 * they add the wrong item. The wrapper is a fixed square whatever is inside it,
 * and the icon fallback is the same square, so a catalog that is half
 * illustrated does not render as a ragged grid.
 *
 * A BROKEN IMAGE FALLS BACK TO THE ICON. image_url can outlive its object — a
 * deleted file, a bucket made private, a CDN hiccup — and the alt-text box that
 * a bare <img> leaves behind is worse than the line icon this replaces.
 *
 * alt="" BECAUSE IT IS DECORATIVE. Every place this renders puts the item's name
 * in text beside it; reading "Sofa, 3-cushion" twice helps nobody.
 */
export function CatalogArt({
  src, icon, size = 30, stroke = 1.6,
}: { src?: string | null; icon?: string | null; size?: number; stroke?: number }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [src]);

  const box: React.CSSProperties = {
    width: size, height: size, flex: `0 0 ${size}px`,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };

  if (!src || failed) {
    return <span style={box} aria-hidden="true"><CatalogIcon name={icon} size={size} stroke={stroke} /></span>;
  }
  return (
    <span style={box}>
      <img
        src={src} alt="" width={size} height={size}
        loading="lazy" decoding="async"
        onError={() => setFailed(true)}
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
    </span>
  );
}
